import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Users } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/Card";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PerpetratorRecord {
  id: number;
  personId: number;
  agencyId: number;
  firstName: string | null;
  lastName: string | null;
  relationship: string | null;
  details: string | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface PerpetratorsListProps {
  personId: string;
}

export function PerpetratorsList({ personId }: PerpetratorsListProps) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingPerp, setEditingPerp] = useState<PerpetratorRecord | null>(
    null,
  );

  const { data: perpsData, isLoading } = useQuery<{
    data: PerpetratorRecord[];
  }>({
    queryKey: ["perpetrators", personId],
    queryFn: () =>
      api.get<{ data: PerpetratorRecord[] }>(
        `/clients/${personId}/perpetrators`,
      ),
    enabled: !!personId,
  });

  const createMutation = useMutation({
    mutationFn: (data: {
      firstName: string;
      lastName: string;
      relationship?: string;
      details?: string;
    }) => api.post(`/clients/${personId}/perpetrators`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["perpetrators", personId],
      });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: number;
      firstName?: string;
      lastName?: string;
      relationship?: string;
      details?: string;
    }) => api.put(`/perpetrators/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["perpetrators", personId],
      });
      setEditingPerp(null);
    },
  });

  const perps = perpsData?.data ?? [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Perpetrators</h2>
        <Button
          size="sm"
          className="gap-2"
          onClick={() => {
            setShowForm(true);
            setEditingPerp(null);
          }}
        >
          <Plus className="h-4 w-4" />
          Add Perpetrator
        </Button>
      </div>

      {/* Create/Edit form */}
      {(showForm || editingPerp) && (
        <Card>
          <CardContent className="p-4">
            <PerpetratorForm
              initial={editingPerp}
              onSubmit={(data) => {
                if (editingPerp) {
                  updateMutation.mutate({ id: editingPerp.id, ...data });
                } else {
                  createMutation.mutate(data);
                }
              }}
              onCancel={() => {
                setShowForm(false);
                setEditingPerp(null);
              }}
              isLoading={
                createMutation.isPending || updateMutation.isPending
              }
            />
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="h-20 animate-pulse rounded-lg border bg-muted"
            />
          ))}
        </div>
      )}

      {/* Empty */}
      {!isLoading && perps.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Users className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No perpetrator records for this client.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Perpetrator cards */}
      {!isLoading &&
        perps.map((perp) => (
          <Card key={perp.id}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-sm font-medium">
                    {perp.firstName} {perp.lastName}
                  </CardTitle>
                  <CardDescription className="mt-0.5">
                    {perp.relationship && (
                      <span className="mr-2">
                        Relationship: {perp.relationship}
                      </span>
                    )}
                    Added{" "}
                    {new Date(perp.createdAt).toLocaleDateString()}
                  </CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setEditingPerp(perp)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardHeader>
            {perp.details && (
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  {perp.details}
                </p>
              </CardContent>
            )}
          </Card>
        ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline form
// ---------------------------------------------------------------------------

interface PerpetratorFormProps {
  initial?: PerpetratorRecord | null;
  onSubmit: (data: {
    firstName: string;
    lastName: string;
    relationship?: string;
    details?: string;
  }) => void;
  onCancel: () => void;
  isLoading: boolean;
}

function PerpetratorForm({
  initial,
  onSubmit,
  onCancel,
  isLoading,
}: PerpetratorFormProps) {
  const [firstName, setFirstName] = useState(initial?.firstName ?? "");
  const [lastName, setLastName] = useState(initial?.lastName ?? "");
  const [relationship, setRelationship] = useState(
    initial?.relationship ?? "",
  );
  const [details, setDetails] = useState(initial?.details ?? "");

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium">
        {initial ? "Edit Perpetrator" : "Add Perpetrator"}
      </h3>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            First Name
          </label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            Last Name
          </label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Last name"
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">
          Relationship to Victim
        </label>
        <input
          type="text"
          value={relationship}
          onChange={(e) => setRelationship(e.target.value)}
          placeholder="e.g., spouse, partner, ex-boyfriend..."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">Details</label>
        <textarea
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          rows={3}
          placeholder="Description, identifying information..."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <div className="flex gap-3">
        <Button
          size="sm"
          onClick={() =>
            onSubmit({
              firstName,
              lastName,
              relationship: relationship || undefined,
              details: details || undefined,
            })
          }
          loading={isLoading}
          disabled={!firstName.trim() || !lastName.trim()}
        >
          {initial ? "Save Changes" : "Add Perpetrator"}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
